/**
 * JourneyPreviewOverlay — team preview of a Client Hub, through one real person's eyes.
 *
 * Opens via CustomEvent('open-journey-preview', { detail: { journeyId, draftProposal? } }).
 * Mounted once in OutlineView.
 *
 * PREVIEW AS A RECIPIENT (2026-10-26, docs/CLIENT_HUB_PREVIEW.md). The team member picks WHOSE Hub
 * to look at from the people who actually have access (the journey's ACTIVE recipients; revoked
 * ones are not offered). The server hands back a short-lived preview pass for that person
 * (services/journey.js#previewAsRecipient) and the overlay renders the client's own page,
 * JourneyView, with the pass in place of their token: same endpoints, same visibility rules, same
 * per-person state (what they've opened, signed, answered, hearted, commented; the PIN-gated items;
 * the signer annotations; the inspiration board). Everything is clickable. Nothing is committed:
 * every write in the browser answers locally when it sees a pass, and the server refuses a pass on
 * every write route. The recipient's own token and PIN never reach the browser.
 *
 * TEAM DRAFT VIEW (the older preview, kept): no particular person, includes unpublished entries
 * and — when opened from a proposal being drafted in the CRM — that draft proposal. It is the only
 * way to see a draft, so it stays as the last choice in the picker and is where the overlay opens
 * when a draft proposal was passed along. Client actions there are no-ops (previewMode).
 */

import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import MistyBackground from '../journey/MistyBackground.jsx'
import JourneyShell from '../journey/JourneyShell.jsx'
import { JourneyView } from '../journey/JourneyPage.jsx'
import { previewJourney, listRecipients, previewAsRecipient } from '../services/journey.js'
import { orderEntries } from '../journey/journeyOrder.js'

const TEAM = 'team'

function Center({ children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
      textAlign: 'center', padding: 24, fontFamily: '"DM Sans", sans-serif', fontSize: 15, color: 'rgba(255,255,255,0.85)',
    }}>{children}</div>
  )
}

function Switch({ on }) {
  return (
    <span style={{ ...SW.track, background: on ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.18)' }}>
      <span style={{ ...SW.knob, transform: on ? 'translateX(16px)' : 'translateX(0)' }} />
    </span>
  )
}

function fmtOpened(iso) {
  if (!iso) return 'Has not opened their link yet'
  const d = new Date(iso)
  return 'Last opened ' + d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

/** The first screen: whose Hub to look at. */
function Picker({ recipients, onPick, hasDraftProposal }) {
  return (
    <div style={P.wrap}>
      <div style={P.card}>
        <div style={P.eyebrow}>Preview the Client Hub</div>
        <div style={P.title}>Whose Hub do you want to see?</div>
        <div style={P.sub}>
          You&rsquo;ll see it exactly as that person does, and you can click through and try things.
          Nothing is saved and nobody is notified.
        </div>
        {recipients === 'loading' && <div style={P.note}>Loading the people with access…</div>}
        {recipients === 'error' && <div style={P.note}>Could not load the people with access.</div>}
        {Array.isArray(recipients) && recipients.length === 0 && (
          <div style={P.note}>No one has access to this Hub yet. Add people under Client Hub &rsaquo; Access.</div>
        )}
        {Array.isArray(recipients) && recipients.map(r => (
          <button key={r.id} type="button" style={P.person} onClick={() => onPick(r.id)}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.16)' }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)' }}>
            <span style={P.avatar}>{(r.name || r.email || '?').trim().charAt(0).toUpperCase()}</span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={P.name}>{r.name || r.email}</span>
              <span style={P.meta}>{r.name && r.email ? r.email + ' · ' : ''}{fmtOpened(r.last_opened_at)}</span>
            </span>
            <span style={P.go}>View as</span>
          </button>
        ))}
        <button type="button" style={P.team} onClick={() => onPick(TEAM)}>
          Team draft view, no particular person{hasDraftProposal ? ', with the proposal being drafted' : ', with unpublished entries'}
        </button>
      </div>
    </div>
  )
}

export default function JourneyPreviewOverlay() {
  const [open, setOpen] = useState(false)
  const [journeyId, setJourneyId] = useState(null)
  const [draftProposal, setDraftProposal] = useState(null)   // { id, type, name, contractFee, retainer, tiered, packages } | null
  const [recipients, setRecipients] = useState('loading')    // 'loading' | 'error' | active recipients[]
  const [viewAs, setViewAs] = useState(null)                 // null (picking) | TEAM | recipient id
  const [pass, setPass] = useState(null)                     // null | 'loading' | 'error' | { token, recipient }
  // Team draft view
  const [showDrafts, setShowDrafts] = useState(true)
  const [data, setData] = useState(null)   // null | 'loading' | 'error' | { project, entries, responses }
  const [index, setIndex] = useState(0)

  useEffect(() => {
    function onOpen(e) {
      const id = e.detail?.journeyId
      if (!id) return
      const draft = e.detail?.draftProposal || null
      setOpen(true); setJourneyId(id); setDraftProposal(draft)
      setShowDrafts(true); setData(null); setIndex(0); setPass(null)
      setRecipients('loading')
      // A proposal still being drafted can only be seen in the team draft view: open there.
      setViewAs(draft ? TEAM : null)
    }
    window.addEventListener('open-journey-preview', onOpen)
    return () => window.removeEventListener('open-journey-preview', onOpen)
  }, [])

  // The people who can actually open this Hub. Revoked recipients are never offered.
  useEffect(() => {
    if (!open || !journeyId) return
    let cancelled = false
    listRecipients(journeyId)
      .then(rs => { if (!cancelled) setRecipients((rs || []).filter(r => r.status === 'active')) })
      .catch(() => { if (!cancelled) setRecipients('error') })
    return () => { cancelled = true }
  }, [open, journeyId])

  // A fresh preview pass each time a person is picked.
  useEffect(() => {
    if (!open || !journeyId || !viewAs || viewAs === TEAM) return
    let cancelled = false
    setPass('loading')
    previewAsRecipient(journeyId, viewAs)
      .then(p => { if (!cancelled) setPass(p && p.token ? p : 'error') })
      .catch(() => { if (!cancelled) setPass('error') })
    return () => { cancelled = true }
  }, [open, journeyId, viewAs])

  // Team draft view: (re)fetch on open and whenever the drafts toggle changes.
  useEffect(() => {
    if (!open || !journeyId || viewAs !== TEAM) return
    let cancelled = false
    setData('loading')
    previewJourney(journeyId, { includeDrafts: showDrafts })
      // Mirror the real page's order (useJourney.js) — by phase then date.
      .then(p => { if (!cancelled) { setData(p && p.entries ? { ...p, entries: orderEntries(p.entries, p.phases || []) } : p); setIndex(0) } })
      .catch(() => { if (!cancelled) setData('error') })
    return () => { cancelled = true }
  }, [open, journeyId, showDrafts, viewAs])

  useEffect(() => {
    if (!open) return
    // Capture phase, so Escape closes the overlay before the outline engine's own handler sees it.
    function onKey(e) {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation()
        setOpen(false)
      }
    }
    window.addEventListener('keydown', onKey, { capture: true })
    return () => window.removeEventListener('keydown', onKey, { capture: true })
  }, [open])

  if (!open) return null

  const people = Array.isArray(recipients) ? recipients : []
  const who = viewAs && viewAs !== TEAM ? people.find(r => r.id === viewAs) : null

  // Whose eyes: switch people (or to the team view) without going back to the picker.
  const viewSelect = (
    <select value={viewAs || ''} onChange={e => setViewAs(e.target.value || null)} style={S.select} title="Whose Hub you are looking at">
      {!viewAs && <option value="">Choose a person…</option>}
      {people.map(r => <option key={r.id} value={r.id}>{r.name || r.email}</option>)}
      <option value={TEAM}>Team draft view</option>
    </select>
  )

  const banner = (
    <div style={{ ...S.banner, background: viewAs === TEAM && showDrafts ? S.banner.background : 'rgba(11,79,66,0.94)' }}>
      <span style={S.dot} />
      <span style={S.bannerLead}>Viewing as</span>
      {viewSelect}
      <span style={S.bannerText}>
        {viewAs === TEAM
          ? (showDrafts
            ? `Team draft view: includes unpublished entries${draftProposal ? ' and the proposal being drafted' : ''}. No client sees this.`
            : 'Team view: published entries only, no particular person.')
          : viewAs
            ? `Exactly what ${who ? (who.name || who.email) : 'this person'} sees. Try anything: nothing is saved, sent or recorded.`
            : 'Pick a person to see their Hub.'}
      </span>
      {viewAs === TEAM && (
        <button onClick={() => setShowDrafts(d => !d)} style={S.draftToggle} title={draftProposal ? 'Toggle unpublished entries and the proposal being drafted' : 'Toggle unpublished draft entries'}>
          <span style={S.draftToggleLabel}>Show drafts</span>
          <Switch on={showDrafts} />
        </button>
      )}
      <button onClick={() => setOpen(false)} style={S.close} title="Close (Esc)">Close ✕</button>
    </div>
  )

  let body = null
  if (!viewAs) {
    body = (<><MistyBackground /><Picker recipients={recipients} onPick={setViewAs} hasDraftProposal={!!draftProposal} /></>)
  } else if (viewAs !== TEAM) {
    if (pass === 'loading' || !pass) body = (<><MistyBackground /><Center>Opening the Hub…</Center></>)
    else if (pass === 'error') body = (<><MistyBackground /><Center>Could not open this person&rsquo;s Hub. Their access may have just been revoked.</Center></>)
    // key: a new person is a fresh page, with nothing carried over from the last one.
    else body = <JourneyView key={pass.token} token={pass.token} banner={null} conceptDrawingHost={false} />
  } else {
    const ready = data && data !== 'loading' && data !== 'error'
    // The draft proposal has no journey_entries row yet — a client-side synthetic entry, never
    // written anywhere. `published: false` gives it JourneyShell's "Draft — not visible" pill.
    const draftEntry = draftProposal ? { id: 'draft-proposal-preview', type: 'draft_proposal_preview', published: false, data: draftProposal } : null
    const entries = ready ? (showDrafts && draftEntry ? [...data.entries, draftEntry] : data.entries) : []
    const total = entries.length
    const responses = ready ? data.responses : []
    const responseFor = id => responses.find(r => r.entry_id === id) || null
    const clamp = i => Math.max(0, Math.min(total - 1, i))
    body = (
      <>
        <MistyBackground />
        {(data === 'loading' || !data) && <Center>Loading preview…</Center>}
        {data === 'error' && <Center>Could not load the preview.</Center>}
        {ready && (
          <JourneyShell
            project={data.project}
            entries={entries}
            index={clamp(index)}
            total={total}
            responseFor={responseFor}
            respond={() => {}}
            goNext={() => setIndex(i => clamp(i + 1))}
            goPrev={() => setIndex(i => clamp(i - 1))}
            goTo={i => setIndex(clamp(i))}
            token={data.token}
            previewMode
          />
        )}
      </>
    )
  }

  return createPortal(
    <div style={S.root}>
      {body}
      {banner}
    </div>,
    document.body
  )
}

const S = {
  root: { position: 'fixed', inset: 0, zIndex: 9400, overflow: 'hidden', background: '#07272D' },
  banner: {
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999, height: 34,
    display: 'flex', alignItems: 'center', gap: 10, padding: '0 16px',
    background: 'rgba(184,84,10,0.92)', color: '#fff',
    fontFamily: '"DM Sans", sans-serif', fontSize: 12, fontWeight: 500,
  },
  dot: { width: 7, height: 7, borderRadius: '50%', background: '#fff', flexShrink: 0, opacity: 0.9 },
  bannerLead: { flexShrink: 0, opacity: 0.85 },
  select: {
    flexShrink: 0, maxWidth: 220, background: 'rgba(255,255,255,0.16)', color: '#fff', border: 'none',
    borderRadius: 4, padding: '4px 8px', fontFamily: '"DM Sans", sans-serif', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  bannerText: { flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  draftToggle: {
    flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 8,
    background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff',
    fontFamily: '"DM Sans", sans-serif', fontSize: 12, fontWeight: 500, padding: '5px 10px',
    borderRadius: 20, cursor: 'pointer',
  },
  draftToggleLabel: { whiteSpace: 'nowrap' },
  close: {
    flexShrink: 0, background: 'rgba(255,255,255,0.18)', border: 'none', color: '#fff',
    fontFamily: '"DM Sans", sans-serif', fontSize: 12, fontWeight: 600, padding: '5px 12px',
    borderRadius: 6, cursor: 'pointer',
  },
}

const P = {
  wrap: { position: 'fixed', inset: 0, zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '54px 16px 24px', overflowY: 'auto' },
  card: {
    width: '100%', maxWidth: 520, padding: '28px 26px', borderRadius: 10,
    background: 'rgba(7,39,45,0.62)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
    border: '0.5px solid rgba(255,255,255,0.18)', color: '#fff', fontFamily: '"DM Sans", sans-serif',
  },
  eyebrow: { fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 500, letterSpacing: '0.24em', textTransform: 'uppercase', opacity: 0.7, marginBottom: 10 },
  title: { fontSize: 20, fontWeight: 500, marginBottom: 8 },
  sub: { fontSize: 13.5, lineHeight: 1.55, color: 'rgba(255,255,255,0.75)', marginBottom: 18 },
  note: { fontSize: 13, color: 'rgba(255,255,255,0.7)', padding: '10px 0' },
  person: {
    width: '100%', display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left', marginBottom: 8,
    padding: '12px 14px', borderRadius: 10, border: '0.5px solid rgba(255,255,255,0.16)',
    background: 'rgba(255,255,255,0.08)', color: '#fff', cursor: 'pointer', transition: 'background 0.15s',
    fontFamily: '"DM Sans", sans-serif',
  },
  avatar: {
    width: 34, height: 34, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(255,255,255,0.18)', fontSize: 14, fontWeight: 600,
  },
  name: { display: 'block', fontSize: 14.5, fontWeight: 600 },
  meta: { display: 'block', fontSize: 12, color: 'rgba(255,255,255,0.65)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  go: { flexShrink: 0, fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.85)' },
  team: {
    marginTop: 10, background: 'transparent', border: 'none', padding: 0, color: 'rgba(255,255,255,0.65)',
    fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, textDecoration: 'underline', cursor: 'pointer', textAlign: 'left',
  },
}

const SW = {
  track: { position: 'relative', display: 'inline-block', width: 30, height: 16, borderRadius: 20, flexShrink: 0, transition: 'background 0.18s' },
  knob: { position: 'absolute', top: 2, left: 2, width: 12, height: 12, borderRadius: '50%', background: '#fff', transition: 'transform 0.18s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' },
}
